import { PlusOutlined } from '@ant-design/icons';
import { ProductSupplier, ProductSupplierOrderChannel, SupplierAdditionalContact } from '@gooduncles/gu-app-schema';
import { Button, Checkbox, Col, Form, Input, Modal, Row, Select, Space, Tag, message } from 'antd';
import { FC, useState } from 'react';
import { orderChannelOptions } from 'src/constants';

import { errorObjectToString, normalizeTel } from 'src/utils/common';
import { createProductSupplier } from 'src/utils/firebase/firebase-short-cut';

const { Option } = Select;

const requiredRule = [{ required: true, message: '필수 입력 항목입니다.' }];
const telRule = {
  min: 10,
  message: '전화번호 형식이 올바르지 않습니다.',
};

const formatBusinessNumber = (value: string) => {
  if (!value) return value;
  const numbers = value.replace(/[^0-9]/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
};

/**
 * ProductSupplier 객체의 필드를 검증하고 기본값 설정
 * deletedAt 필드는 제외하고 처리
 * 필수 필드가 없으면 오류를 발생시킴
 */
const ensureProductSupplierFields = (
  data: Partial<ProductSupplier>,
  additionalContacts: SupplierAdditionalContact[]
): ProductSupplier => {
  // 필수 필드 검증
  const requiredFields: Array<{ key: keyof ProductSupplier; label: string }> = [
    { key: 'name', label: '상호명' },
    { key: 'groupId', label: '그룹' },
    { key: 'primaryContact', label: '대표 연락처' },
    { key: 'orderChannel', label: '발주 채널' },
  ];
  for (const field of requiredFields) {
    if (!data[field.key]) {
      throw new Error(`${field.label}은(는) 필수 입력 항목입니다.`);
    }
  }

  // 전화번호 검증
  if (data.primaryContact && data.primaryContact.length < 10) {
    throw new Error('대표 연락처 형식이 올바르지 않습니다. (최소 10자)');
  }

  return {
    name: data.name || '',
    groupId: data.groupId || '',
    primaryContact: data.primaryContact || '',
    orderChannel: data.orderChannel || ProductSupplierOrderChannel.ETC,
    ownerName: data.ownerName || '',
    businessNumber: data.businessNumber || '',
    address: data.address || '',
    businessType: data.businessType || '',
    businessCategory: data.businessCategory || '',
    hidden: data.hidden || false,
    additionalContacts: additionalContacts,
    deletedAt: null,
  };
};

const CreateSupplierModal: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [additionalContacts, setAdditionalContacts] = useState<SupplierAdditionalContact[]>([]);
  const [contactNumber, setContactNumber] = useState<string>('');
  const [contactLabel, setContactLabel] = useState<string>('');

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setAdditionalContacts([]);
    setIsModalOpen(false);
  };

  const handleOk = () => {
    form.submit();
  };

  const onFinish = async (values: Partial<ProductSupplier>) => {
    try {
      setIsLoading(true);
      const newProductSupplier = ensureProductSupplierFields(values, additionalContacts);
      await createProductSupplier(newProductSupplier);

      message.success('거래처가 성공적으로 추가되었습니다.');
      handleCancel();
    } catch (error) {
      console.error(error);
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddContact = () => {
    if (!contactLabel) {
      message.error('연락처 구분을 입력해주세요.');
      return;
    }

    if (!contactNumber) {
      message.error('연락처 번호를 입력해주세요.');
      return;
    }

    // 전화번호 유효성 검사
    if (contactNumber.length < 10) {
      message.error('연락처 번호는 최소 10자 이상이어야 합니다.');
      return;
    }

    const newContact: SupplierAdditionalContact = {
      number: contactNumber,
      label: contactLabel,
    };
    setAdditionalContacts([...additionalContacts, newContact]);
    setContactNumber('');
    setContactLabel('');
  };

  return (
    <>
      <Button type='primary' onClick={showModal}>
        거래처 추가
      </Button>
      <Modal
        title='거래처 추가'
        open={isModalOpen}
        centered
        onCancel={handleCancel}
        onOk={handleOk}
        okText='추가'
        cancelText='취소'
        width={700}
        confirmLoading={isLoading}
        okButtonProps={{ loading: isLoading }}
        cancelButtonProps={{ disabled: isLoading }}>
        <Form form={form} layout='vertical' onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='name' label='상호명' rules={requiredRule}>
                <Input placeholder='예: 엉클푸드' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='groupId' label='그룹' rules={requiredRule}>
                <Input placeholder='관리상 분류할 때 사용하는 그룹코드' />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='primaryContact'
                label='대표 연락처'
                rules={[...requiredRule, telRule]}
                getValueFromEvent={(e) => normalizeTel(e.target.value)}>
                <Input placeholder='예: 01012345678' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='orderChannel' label='발주 채널' rules={requiredRule}>
                <Select placeholder='발주 채널을 선택하세요'>
                  {orderChannelOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='ownerName' label='대표자 성명'>
                <Input placeholder='예: 엉클조' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='businessNumber'
                label='사업자 등록번호'
                getValueFromEvent={(e) => formatBusinessNumber(e.target.value)}>
                <Input placeholder='예: 123-45-67890' />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label='추가 연락처 목록'>
            <Space style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '8px' }}>
              {additionalContacts.map((contact, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => {
                    const newContacts = [...additionalContacts];
                    newContacts.splice(index, 1);
                    setAdditionalContacts(newContacts);
                  }}>
                  {`${contact.label}: ${contact.number}`}
                </Tag>
              ))}
            </Space>
            <Space>
              <Input
                placeholder='구분 (예: 매니저)'
                value={contactLabel}
                onChange={(e) => setContactLabel(e.target.value)}
                style={{ width: 150 }}
              />
              <Input
                placeholder='연락처 번호'
                value={contactNumber}
                minLength={10}
                status={contactNumber && contactNumber.length < 10 ? 'error' : ''}
                onChange={(e) => setContactNumber(normalizeTel(e.target.value))}
                style={{ width: 150 }}
              />
              <Button
                type='primary'
                icon={<PlusOutlined />}
                onClick={handleAddContact}
                disabled={!contactLabel || !contactNumber || contactNumber.length < 10}>
                추가
              </Button>
            </Space>
          </Form.Item>

          <Form.Item name='address' label='사업장 주소'>
            <Input placeholder='예: 경기도 하남시 감초로 75 (감북동286-6번지)' />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='businessType' label='업태'>
                <Input placeholder='예: 도매, 제조업' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='businessCategory' label='종목'>
                <Input placeholder='예: 냉동식품, 조미식품, 소스류 외' />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='hidden' valuePropName='checked'>
            <Checkbox>숨김처리</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CreateSupplierModal;
